import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { $createParagraphNode, $createTextNode, $getNodeByKey, $getRoot } from 'lexical';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import * as zod from 'zod';
import { z } from 'zod';
import {
  ContactInteractionMessageTemplate,
  CreateContactInteractionMessageTemplateInput,
  Language,
  Mutation,
  MutationCreateContactInteractionMessageTemplateArgs,
  MutationUpdateContactInteractionMessageTemplateArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useActiveDepartmentSelectOptions } from 'src/components/domain/departments/hooks/use-active-department-select-options';
import { useSetFormFieldValue } from 'src/components/filters/helpers';
import { MessagePreview } from 'src/components/general/display/message-preview';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { LexicalTextEditor } from 'src/components/general/inputs/lexical-text-editor';
import { Radio } from 'src/components/general/inputs/radio';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { TextInput } from 'src/components/general/inputs/text-input';
import {
  FooterActionButton,
  ModalBodyContainer,
  ModalFooterContainer,
  StyledTemplatePickerModal,
  TemplatePickerModalHeader,
} from 'src/components/general/modals/template-picker-modal';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { CreateTemplateCustomPlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/create-custom-template-placeholder-node';
import { CreateTemplatePresetPlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/create-preset-template-placeholder-node';
import { AiToolsPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/ai-tools-picker-plugin';
import { CreateTemplatePlaceholderPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/create-template-placeholder-plugin';
import { EmojiPickerPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/emoji-picker-plugin';
import { ContactTextPlaceholderKey } from 'src/components/pages/conversations/components/general/text-placeholders-helpers';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { VisibilityHandler } from 'src/hooks/use-visible';
import { labelTextStyle } from 'src/theme/sub-themes/inputs-theme';

const CREATE_CONTACT_INTERACTION_MESSAGE_TEMPLATE_MUTATION = gql`
  mutation CreateContactInteractionMessageTemplate(
    $input: CreateContactInteractionMessageTemplateInput!
  ) {
    createContactInteractionMessageTemplate(input: $input) {
      id
    }
  }
`;

const UPDATE_CONTACT_INTERACTION_MESSAGE_TEMPLATE_MUTATION = gql`
  mutation UpdateContactInteractionMessageTemplate(
    $input: UpdateContactInteractionMessageTemplateInput!
  ) {
    updateContactInteractionMessageTemplate(input: $input) {
      id
      name
      content
      messagePreview
      language
      shareWith
      department {
        id
        name
      }
    }
  }
`;

export enum TemplateShareWithValue {
  EVERYONE = 'EVERYONE',
  ONLY_ME = 'ONLY_ME',
}

export enum TemplateLanguageValue {
  SPANISH = 'SPANISH',
  ENGLISH = 'ENGLISH',
}

const lexicalEditorConfig = {
  namespace: 'UpsertContactInteractionMessageTemplateEditor',
  theme: {
    placeholder: 'editor-placeholder',
    paragraph: 'editor-paragraph',
  },
  onError(error) {
    throw error;
  },
  nodes: [CreateTemplateCustomPlaceholderNode, CreateTemplatePresetPlaceholderNode],
};

const TemplatePickerBottomDrawer = styled(BottomDrawer)`
  .ant-drawer-body {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

const ModalBodyCard = styled(FlexRow)`
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px 20px 25px;
  justify-content: space-between;
`;

const ModalBodyCardRightRail = styled(FlexColumn)`
  margin-left: 40px;
  width: 238px;
`;

const ModalBodyCardLeftRail = styled(FlexColumn)`
  width: 100%;
  max-width: 448px;
`;

const StyledLexicalEditor = styled(LexicalTextEditor)`
  .text-input-container {
    margin: 0;
    padding: 10px 12px;
    height: 174px;

    .editor-input {
      height: 154px;
    }

    .placeholder-text {
      top: 10px;
    }
  }
`;

const TipContainer = styled.p`
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.secondaryTopaz60};
  background: ${({ theme }) => theme.colors.secondaryTopaz10};
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  font-style: normal;
  line-height: 15px;
  margin: 10px 0 0;
  padding: 9.5px 10px;
  height: 34px;
`;

const TipStrongText = styled.span`
  -webkit-text-stroke-width: 1px;
  -webkit-text-stroke-color: rgba(0, 0, 0, 0.1);
`;

const StyledForm = styled.form``;

const InputsContainer = styled(FlexColumn)`
  padding: 20px 20px 25px;

  ${up('md')} {
    padding: 0;
  }

  & > * {
    &:not(:last-child) {
      margin-bottom: 17px;
    }
  }
`;

const InputLabel = styled.span`
  ${labelTextStyle};
  line-height: 14px;
`;

const AvailableForInputsContainer = styled(FlexRow)`
  margin-top: 10px;

  & > * {
    margin-right: 15px;
  }
`;

const MessagePreviewTitle = styled.p`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 18px;
  margin-bottom: 10px;
`;

const ContentInputContainer = styled(FlexColumn)`
  padding-bottom: 5px;

  & > * {
    margin-bottom: 0;
  }
`;

const MESSAGE_PREVIEW_PRESET_REGEX_MAP = {
  firstName: /\{\{contact_first_name:.+?\}\}/g,
  lastName: /\{\{contact_last_name:.+?\}\}/g,
  phoneNumber: /\{\{contact_phone_number:.+?\}\}/g,
  email: /\{\{contact_email:.+?\}\}/g,
};

const MESSAGE_PREVIEW_PRESET_KEYS_VALUES_MAP = {
  firstName: 'Pedro',
  lastName: 'Ortiz',
  phoneNumber: '829-350-0909',
  email: 'pedro_ortiz@gmail.com',
};

const MESSAGE_PREVIEW_KEYS_REPLACEMENTS_MAP = {
  firstName: `{{${ContactTextPlaceholderKey.firstName}}}`,
  lastName: `{{${ContactTextPlaceholderKey.lastName}}}`,
  phoneNumber: `{{${ContactTextPlaceholderKey.phoneNumber}}}`,
  email: `{{${ContactTextPlaceholderKey.email}}}`,
};

const CUSTOM_PARAMETER_REGEX = /\{\{(.+?):\d+\}\}/g;

const messageTemplateFormSchema = zod.object({
  name: zod.string(),
  departmentId: zod.number().nullable(),
  language: z.union([z.literal('ENGLISH'), z.literal('SPANISH')]),
  content: zod.string(),
  shareWith: z.union([z.literal('EVERYONE'), z.literal('ONLY_ME')]),
  messagePreview: zod.string(),
});

const defaultFormValues = {
  content: undefined,
  departmentId: null,
  name: undefined,
  language: undefined,
  shareWith: TemplateShareWithValue.EVERYONE,
  messagePreview: undefined,
};

type TemplatePickerModalProps = {
  visibilityHandler: VisibilityHandler;
  template?: ContactInteractionMessageTemplate;
  onCreate: VoidFunction;
};

export const UpsertMessageTemplateModal = ({
  visibilityHandler,
  template,
  onCreate,
}: TemplatePickerModalProps) => {
  const { t } = useTranslation(['conversations', 'common']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const { loadingDepartmentOptions, departmentOptions } =
    useActiveDepartmentSelectOptions();
  const formRef = useRef<HTMLFormElement>(null);
  const handleGraphQLError = useHandleGraphQLError();
  const { id: institutionId } = useCurrentInstitution();
  const showNotification = useShowNotification();
  const isNew = !template?.id;
  const { hasPermission } = useAuthorization();
  const isManager = hasPermission(
    'platform:company.communication_interaction.template:share:all',
  );

  type FormValues = Partial<zod.infer<typeof messageTemplateFormSchema>>;

  const form = useForm<FormValues>({
    resolver: zodResolver(messageTemplateFormSchema),
    defaultValues: {
      ...defaultFormValues,
      shareWith: isManager ? 'EVERYONE' : 'ONLY_ME',
    },
  });

  const initialState = useMemo(() => {
    if (!template) {
      return undefined;
    }

    const { department, name, content, messagePreview, language, shareWith } = template;

    return {
      name,
      departmentId: department?.id,
      content,
      language: language as Language,
      messagePreview,
      shareWith,
    };
  }, [template]);

  useEffect(() => {
    if (initialState) {
      form.reset(initialState);
    }
  }, [form, initialState]);

  const { setFormFieldValue } = useSetFormFieldValue<FormValues>({
    defaultValues: defaultFormValues,
    setValue: form.setValue,
  });

  const handleModalClose = useCallback(() => {
    visibilityHandler.close();
    form.reset(defaultFormValues);
  }, [form, visibilityHandler]);

  const [createContactInteractionMessageTemplate] = useMutation<
    Pick<Mutation, 'createContactInteractionMessageTemplate'>,
    MutationCreateContactInteractionMessageTemplateArgs
  >(CREATE_CONTACT_INTERACTION_MESSAGE_TEMPLATE_MUTATION, {
    onError: handleGraphQLError,
  });

  const [updateContactInteractionMessageTemplate] = useMutation<
    Pick<Mutation, 'updateContactInteractionMessageTemplate'>,
    MutationUpdateContactInteractionMessageTemplateArgs
  >(UPDATE_CONTACT_INTERACTION_MESSAGE_TEMPLATE_MUTATION, {
    onError: handleGraphQLError,
  });

  const submitHandler: SubmitHandler<FormValues> = useCallback(
    async (formValues) => {
      const input = {
        ...formValues,
        messagePreview: simplifyMessagePreview(formValues?.messagePreview ?? ''),
        companyId: institutionId,
      } as CreateContactInteractionMessageTemplateInput;

      if (isNew) {
        await createContactInteractionMessageTemplate({
          variables: {
            input,
          },
          onCompleted: () => {
            handleModalClose();
            showNotification({
              title: t(
                'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.createdSuccessMessage',
              ),
              type: 'success',
            });
            onCreate();
          },
        });
      } else {
        await updateContactInteractionMessageTemplate({
          variables: {
            input: {
              ...input,
              id: template.id,
            },
          },
          onCompleted: () => {
            handleModalClose();
            showNotification({
              title: t(
                'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.updatedSuccessMessage',
              ),
              type: 'success',
            });
          },
        });
      }
    },
    [
      createContactInteractionMessageTemplate,
      handleModalClose,
      institutionId,
      isNew,
      onCreate,
      showNotification,
      t,
      template?.id,
      updateContactInteractionMessageTemplate,
    ],
  );

  const languageOptions: SelectOption<Language>[] = useMemo(
    () => [
      { label: t('common:languages.english'), value: TemplateLanguageValue.ENGLISH },
      { label: t('common:languages.spanish'), value: TemplateLanguageValue.SPANISH },
    ],
    [t],
  );

  /**
   * Formats a message preview by replacing placeholders with example values
   * @param rawMessage - The raw message content with placeholders
   * @returns The formatted message with example values
   */
  const formatMessagePreview = (rawMessage: string): string => {
    let previewMessage = rawMessage;

    Object.entries(MESSAGE_PREVIEW_PRESET_REGEX_MAP).forEach(([key, value]) => {
      previewMessage = previewMessage.replaceAll(
        value,
        MESSAGE_PREVIEW_PRESET_KEYS_VALUES_MAP[key],
      );
    });

    const customParameters = previewMessage.matchAll(CUSTOM_PARAMETER_REGEX);

    customParameters.forEach(([paramKey, textValue]) => {
      previewMessage = previewMessage.replace(paramKey, textValue);
    });

    return previewMessage;
  };

  /**
   * Formats a message for AI tools by simplifying placeholder syntax
   * @param rawMessage - The raw message content with placeholders
   * @returns The formatted message with simplified placeholders
   */
  const simplifyMessagePreview = (rawMessage: string): string => {
    let previewMessage = rawMessage;

    Object.entries(MESSAGE_PREVIEW_PRESET_REGEX_MAP).forEach(([key, value]) => {
      previewMessage = previewMessage.replaceAll(
        value,
        MESSAGE_PREVIEW_KEYS_REPLACEMENTS_MAP[key],
      );
    });

    const customParameters = previewMessage.matchAll(CUSTOM_PARAMETER_REGEX);

    customParameters.forEach(([paramKey, keyValue]) => {
      previewMessage = previewMessage.replace(paramKey, `{{${keyValue}}}`);
    });

    return previewMessage;
  };

  const footerContent = useMemo(() => {
    const buttonLabelKey = isNew
      ? 'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.create.buttonLabel'
      : 'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.update.buttonLabel';

    return (
      <ModalFooterContainer>
        <FooterActionButton
          data-lgg-id="upsert-message-template-modal-submit-button"
          variant="primary"
          type="submit"
          onClick={async () => {
            if (!breakpointUpMd) {
              formRef.current?.requestSubmit();
            }
          }}
        >
          {t(buttonLabelKey)}
        </FooterActionButton>
      </ModalFooterContainer>
    );
  }, [breakpointUpMd, isNew, t]);

  const formContent = useMemo(() => {
    return (
      <InputsContainer>
        <Controller
          control={form.control}
          name="name"
          render={({ field, fieldState }) => {
            return (
              <TextInput
                {...field}
                data-lgg-id="upsert-message-template-name-input"
                error={Boolean(fieldState.error)}
                label={t(
                  'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.templateName.label',
                )}
                placeholder={t(
                  'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.templateName.placeholder',
                )}
                autoComplete="off"
                reserveErrorArea={false}
                showErrorMessage={false}
                value={field.value ?? undefined}
                onChange={(e) => {
                  const value = e.target.value;

                  setFormFieldValue(field.name, value.length > 0 ? value : undefined);
                }}
              />
            );
          }}
        />
        <Controller
          control={form.control}
          name="departmentId"
          render={({ field, fieldState }) => {
            const selectedOption =
              departmentOptions.filter(
                ({ value: optionValue }) => optionValue === field.value,
              ) ?? null;

            return (
              <Select<SelectOption<number>>
                name="upsert-message-template-department-input"
                value={selectedOption}
                options={departmentOptions}
                isLoading={loadingDepartmentOptions}
                isSearchable={false}
                label={t(
                  'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.department.label',
                )}
                placeholder={t(
                  'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.department.placeholder',
                )}
                hasError={Boolean(fieldState.error)}
                onChange={(option) => {
                  setFormFieldValue(field.name, option?.value ?? null);
                }}
              />
            );
          }}
        />
        <Controller
          control={form.control}
          name="language"
          render={({ field, fieldState }) => {
            const selectedOption =
              languageOptions.filter(
                ({ value: optionValue }) => optionValue === field.value,
              ) ?? null;

            return (
              <Select<SelectOption<Language>>
                name="upsert-message-template-language-input"
                value={selectedOption}
                options={languageOptions}
                isSearchable={false}
                hasError={Boolean(fieldState.error)}
                label={t(
                  'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.language.label',
                )}
                placeholder={t(
                  'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.language.placeholder',
                )}
                onChange={(option) => {
                  setFormFieldValue(field.name, option?.value ?? undefined);
                }}
              />
            );
          }}
        />
        <ContentInputContainer>
          <Controller
            control={form.control}
            name="content"
            render={({ fieldState }) => {
              return (
                <>
                  <InputLabel>
                    {t(
                      'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.templateContent.label',
                    )}
                  </InputLabel>
                  <StyledLexicalEditor
                    hasError={Boolean(fieldState.error)}
                    editorConfig={{
                      ...lexicalEditorConfig,
                      editorState: initialState?.content,
                    }}
                    placeholderText={t(
                      'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.templateContent.placeholder',
                    )}
                    onChange={(editorState) => {
                      editorState.read(() => {
                        const root = $getRoot();
                        const textContent = root.getTextContent();

                        setFormFieldValue(
                          'content',
                          textContent.length > 0
                            ? JSON.stringify(editorState.toJSON())
                            : undefined,
                        );

                        setFormFieldValue('messagePreview', textContent);

                        if (fieldState.error) {
                          form.clearErrors('content');
                        }
                      });
                    }}
                    bottomExtraContent={null}
                    testId="upsert-message-template-content-input"
                    plugins={
                      <>
                        <EmojiPickerPlugin />
                        <CreateTemplatePlaceholderPlugin />
                        <AiToolsPlugin
                          onReplace={(text, editor) => {
                            const matchingKeys = text.match(/\{\{.*?\}\}/g);

                            const getNodeKeyFromId = (id: string) => {
                              const matcher = id.match(/\{\{.+?:(\d+)\}\}/);

                              if (matcher) {
                                return matcher[1];
                              }

                              return null;
                            };

                            editor.update(() => {
                              const editorParagraph = $createParagraphNode();

                              let resultText = text;

                              matchingKeys?.forEach((key) => {
                                const indexOfKey = resultText.indexOf(key);
                                const parameterKey = getNodeKeyFromId(key);

                                const textNode = $createTextNode(
                                  resultText.slice(0, indexOfKey),
                                );

                                editorParagraph.append(textNode);

                                if (parameterKey) {
                                  const parameterNode = $getNodeByKey(parameterKey);

                                  if (parameterNode) {
                                    editorParagraph.append(parameterNode);
                                  }
                                }

                                resultText = resultText.slice(indexOfKey + key.length);
                              });

                              // If there is still text available, add it to the paragraph
                              editorParagraph.append($createTextNode(resultText));

                              const parentNode = $getRoot()
                                .getFirstChild()
                                ?.replace(editorParagraph);

                              parentNode?.selectEnd();
                            });
                          }}
                          onSetPreview={simplifyMessagePreview}
                        />
                      </>
                    }
                  />
                </>
              );
            }}
          />
        </ContentInputContainer>
        {isManager ? (
          <Controller
            control={form.control}
            name="shareWith"
            defaultValue={TemplateShareWithValue.EVERYONE}
            render={({ field }) => {
              return (
                <FlexColumn>
                  <InputLabel>
                    {t(
                      'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.templateAvailableFor.label',
                    )}
                  </InputLabel>
                  <AvailableForInputsContainer>
                    <Radio
                      value={TemplateShareWithValue.EVERYONE}
                      text={t(
                        'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.templateAvailableFor.options.everyone',
                      )}
                      testId="upsert-message-template-available-for-everyone-radio"
                      name="upsert-message-template-available-for-everyone-radio"
                      onChange={() => {
                        setFormFieldValue('shareWith', TemplateShareWithValue.EVERYONE);
                      }}
                      checked={field.value === TemplateShareWithValue.EVERYONE}
                    />
                    <Radio
                      value={TemplateShareWithValue.ONLY_ME}
                      text={t(
                        'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.fields.templateAvailableFor.options.onlyMe',
                      )}
                      testId="upsert-message-template-available-for-only-me-radio"
                      name="upsert-message-template-available-for-only-me-radio"
                      onChange={() => {
                        setFormFieldValue('shareWith', TemplateShareWithValue.ONLY_ME);
                      }}
                      checked={field.value === TemplateShareWithValue.ONLY_ME}
                    />
                  </AvailableForInputsContainer>
                </FlexColumn>
              );
            }}
          />
        ) : null}
      </InputsContainer>
    );
  }, [
    departmentOptions,
    form,
    initialState?.content,
    isManager,
    languageOptions,
    loadingDepartmentOptions,
    setFormFieldValue,
    t,
  ]);

  const modalTitle = useMemo(
    () =>
      t(
        isNew
          ? 'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.create.title'
          : 'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.update.title',
      ),
    [isNew, t],
  );

  if (!visibilityHandler.visible) {
    return null;
  }

  return breakpointUpMd ? (
    <StyledTemplatePickerModal
      onClose={handleModalClose}
      visible
      width={806}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
      wrapProps={{
        'data-lgg-id': 'upsert-message-template-modal',
      }}
    >
      <StyledForm {...form} ref={formRef} onSubmit={form.handleSubmit(submitHandler)}>
        <TemplatePickerModalHeader title={modalTitle} />
        <ModalBodyContainer>
          <ModalBodyCard>
            <ModalBodyCardLeftRail>{formContent}</ModalBodyCardLeftRail>
            <ModalBodyCardRightRail>
              <Controller
                control={form.control}
                name="messagePreview"
                render={({ field }) => {
                  return field.value ? (
                    <>
                      <MessagePreviewTitle>
                        {t('common:messagePreview')}
                      </MessagePreviewTitle>
                      <MessagePreview
                        message={formatMessagePreview(field.value)}
                        testId="upsert-message-template-message-preview"
                      />
                    </>
                  ) : (
                    <></>
                  );
                }}
              />
            </ModalBodyCardRightRail>
          </ModalBodyCard>
          <TipContainer>
            <Trans
              i18nKey="conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.modalTip"
              components={{
                strong: <TipStrongText />,
              }}
            />
          </TipContainer>
        </ModalBodyContainer>
        {footerContent}
      </StyledForm>
    </StyledTemplatePickerModal>
  ) : (
    <TemplatePickerBottomDrawer
      fullHeight
      addScrollbarToBody={false}
      onClose={handleModalClose}
      title={modalTitle}
      visible={visibilityHandler.visible}
      footer={footerContent}
    >
      <StyledForm {...form} ref={formRef} onSubmit={form.handleSubmit(submitHandler)}>
        {formContent}
      </StyledForm>
    </TemplatePickerBottomDrawer>
  );
};
